'use client';
import React, { useState } from 'react';
import { Copy, Eye, EyeOff, Plus, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Backend integration: GET /api/keys to list, POST /api/keys to create, DELETE /api/keys/:id
const apiKeys = [
  { id: 'key-001', name: 'ServiceJockey Production', key: 'fj_live_sk_4xR9mKpQ2wNvLzT8cBdY7hJeUoAs3Fg', created: 'Mar 12, 2026', lastUsed: '13:54 today', scopes: ['jobs:read', 'jobs:write', 'assets:read'], active: true },
  { id: 'key-002', name: 'RigJockey Sync', key: 'fj_live_sk_7nXkWqZ1vMpRsT4bGcD9eHjLuOa2Fy', created: 'Feb 28, 2026', lastUsed: '13:38 today', scopes: ['jobs:read', 'assets:read', 'assets:write'], active: true },
  { id: 'key-003', name: 'Mobile App (iOS)', key: 'fj_live_sk_2pYmNsV5wKtQxU8cBaE3gLjRoZi6Hd', created: 'Jan 15, 2026', lastUsed: 'Mar 27, 2026', scopes: ['tracking:read', 'jobs:read'], active: true },
  { id: 'key-004', name: 'Legacy Integration (Deprecated)', key: 'fj_live_sk_9aFbCdEfGhIjKlMn', created: 'Nov 02, 2025', lastUsed: 'Dec 14, 2025', scopes: ['jobs:read'], active: false },
];

export default function ApiKeyPanel() {
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  const toggleVisibility = (id: string) => {
    const next = new Set(visibleKeys);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setVisibleKeys(next);
  };

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key).then(() => toast.success('API key copied to clipboard'));
  };

  const maskKey = (key: string) => `${key.slice(0, 12)}${'•'.repeat(24)}${key.slice(-4)}`;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-zinc-200">API Keys</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Manage authentication keys for external integrations and mobile apps</p>
        </div>
        <button
          onClick={() => toast.success('New API key created')}
          className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-semibold rounded-lg transition-all active:scale-95"
        >
          <Plus size={13} /> Generate New Key
        </button>
      </div>

      {/* Security notice */}
      <div className="bg-amber-950/20 border border-amber-500/20 rounded-xl px-4 py-3 text-xs text-amber-300 flex items-start gap-2">
        <span className="text-amber-400 mt-0.5">⚠</span>
        Treat API keys like passwords. Never share them in plaintext or commit them to version control. Rotate keys that may have been exposed.
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Key Name', 'API Key', 'Scopes', 'Created', 'Last Used', 'Status', 'Actions'].map((h) => (
                <th key={`api-h-${h}`} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {apiKeys.map((k) => {
              const isVisible = visibleKeys.has(k.id);
              return (
                <tr key={k.id} className={`group hover:bg-zinc-800/40 transition-colors ${!k.active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 text-xs font-semibold text-zinc-200">{k.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-[10px] font-mono text-zinc-400 bg-zinc-800 px-2 py-1 rounded max-w-[200px] truncate">
                        {isVisible ? k.key : maskKey(k.key)}
                      </code>
                      <button onClick={() => toggleVisibility(k.id)} className="text-zinc-600 hover:text-zinc-400 transition-colors">
                        {isVisible ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                      <button onClick={() => copyKey(k.key)} className="text-zinc-600 hover:text-amber-400 transition-colors">
                        <Copy size={12} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {k.scopes.map((s) => (
                        <span key={`scope-${k.id}-${s}`} className="text-[9px] bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-500 font-mono">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono">{k.created}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono">{k.lastUsed}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-medium ${k.active ? 'text-emerald-400' : 'text-zinc-600'}`}>
                      {k.active ? '● Active' : '● Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => toast.info('Key rotated')} className="p-1.5 rounded-md text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-all" title="Rotate key">
                        <RefreshCw size={12} />
                      </button>
                      <button onClick={() => toast.error('API key revoked')} className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Revoke key — this cannot be undone">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}